import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, X, Wand2 } from "lucide-react";

const EXAMPLES = [
  "Crée une page moderne pour présenter les artisans partenaires de PROXIreduc à Chartres, avec une présentation chaleureuse, des atouts, des avis et un bouton pour demander un devis.",
  "Page d'accueil pour une campagne « Soutenons nos commerces de Dreux » avec accroche, 3 avantages, témoignages et appel à l'action.",
];

export default function AiModal({ open, onClose, currentBlocks, onApply }) {
  const [prompt, setPrompt] = useState("");
  const [mode, setMode] = useState(currentBlocks && currentBlocks.length ? "rewrite" : "create");
  const [busy, setBusy] = useState(false);
  // À chaque ouverture, proposer le bon mode selon le contenu existant
  useEffect(() => { if (open) setMode(currentBlocks && currentBlocks.length ? "rewrite" : "create"); }, [open, currentBlocks]);
  if (!open) return null;

  const run = async () => {
    if (!prompt.trim()) { toast.error("Décrivez la page souhaitée."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cms/ai/generate", { prompt, mode, blocks: mode === "rewrite" ? currentBlocks : [] });
      if (!data.sections || !data.sections.length) { toast.error("L'IA n'a rien renvoyé, reformulez."); }
      else { onApply(data.sections); toast.success("Brouillon généré par l'IA — à vous de l'ajuster."); onClose(); }
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="ai-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="font-heading font-bold text-slate-900 flex items-center gap-2"><Sparkles size={18} className="text-[#E05638]" /> Assistant IA</div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          {currentBlocks && currentBlocks.length > 0 && (
            <div className="inline-flex rounded-lg bg-slate-100 p-1 text-sm">
              <button onClick={() => setMode("create")} className={`px-3 py-1 rounded-md font-semibold ${mode === "create" ? "bg-white shadow text-slate-900" : "text-slate-500"}`} data-testid="ai-mode-create">Nouvelle page</button>
              <button onClick={() => setMode("rewrite")} className={`px-3 py-1 rounded-md font-semibold ${mode === "rewrite" ? "bg-white shadow text-slate-900" : "text-slate-500"}`} data-testid="ai-mode-rewrite">Améliorer la page actuelle</button>
            </div>
          )}
          <Textarea rows={5} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Décrivez la page que vous souhaitez…" data-testid="ai-prompt" />
          <div className="flex flex-wrap gap-1.5">
            {EXAMPLES.map((ex, i) => (
              <button key={i} onClick={() => setPrompt(ex)} className="text-[11px] text-left px-2 py-1 rounded-lg border border-slate-200 text-slate-500 hover:border-[#E05638] hover:text-[#E05638]" data-testid={`ai-example-${i}`}>{ex.slice(0, 48)}…</button>
            ))}
          </div>
          <p className="text-xs text-slate-400">Le résultat est placé dans le brouillon ; rien n'est publié sans votre validation.</p>
          <Button onClick={run} disabled={busy} className="w-full bg-[#E05638] text-white" data-testid="ai-generate">
            <Wand2 size={16} className="mr-1.5" /> {busy ? "Génération en cours…" : "Générer"}
          </Button>
        </div>
      </div>
    </div>
  );
}
